import type { Sprite, Texture } from "pixi.js";

/**
 * Generic spritesheet-style frame sequence player. Cycles a target `Sprite` through an ordered
 * `Texture[]` at a configured fps via `update(dt)` (the OWNER ticks it), supporting both looping
 * ambient VFX (`loop=true`) and one-shot triggered VFX (`loop=false`, caller invokes `play(onComplete)`).
 *
 * Frames must be untrimmed — trimmed frames have per-frame-varying size/offset that breaks
 * positional consistency across the sequence.
 */
export class FrameSequencePlayer {
  public frames: Texture[] = [];
  public fps: number = 24;
  public sprite: Sprite | null = null;
  /** If true, restarts at frame 0 after the last frame; otherwise stops on the last frame. */
  public loop: boolean = true;
  /** Frame count over which the sprite ramps from alpha 0 to 1 at the start. 0 disables fade-in. */
  public fadeInFrames: number = 0;
  /** Frame count over which the sprite ramps from alpha 1 to 0 at the end. 0 disables fade-out. */
  public fadeOutFrames: number = 0;

  private elapsedSeconds: number = 0;
  private currentIndex: number = 0;
  private isPlaying: boolean = false;
  private completionCallback: (() => void) | undefined = undefined;

  public constructor(init?: Partial<Pick<FrameSequencePlayer, "frames" | "fps" | "sprite" | "loop" | "fadeInFrames" | "fadeOutFrames">>) {
    if (init) Object.assign(this, init);
  }

  /** Resets to frame 0 and starts ticking. `onComplete` is invoked when a non-looping sequence reaches the last frame. */
  public play(onComplete?: () => void): void {
    if (this.frames.length === 0 || !this.sprite) {
      onComplete?.();
      return;
    }

    this.elapsedSeconds = 0;
    this.currentIndex = 0;
    this.sprite.texture = this.frames[0];
    this.applyFadeOpacity(0);
    this.completionCallback = onComplete;
    this.isPlaying = true;
  }

  /** Halts playback without firing the completion callback. */
  public stop(): void {
    this.isPlaying = false;
    this.completionCallback = undefined;
  }

  public pause(): void {
    this.isPlaying = false;
  }

  public resume(): void {
    if (this.frames.length > 0 && this.sprite) {
      this.isPlaying = true;
    }
  }

  public getIsPlaying(): boolean {
    return this.isPlaying;
  }

  public update(dt: number): void {
    if (!this.isPlaying || this.frames.length === 0 || !this.sprite || this.fps <= 0) {
      return;
    }

    this.elapsedSeconds += dt;
    const frameDuration = 1 / this.fps;

    // Advance as many frames as elapsed time covers — a long dt steps multiple frames per tick.
    while (this.elapsedSeconds >= frameDuration) {
      this.elapsedSeconds -= frameDuration;
      this.currentIndex++;

      if (this.currentIndex >= this.frames.length) {
        if (this.loop) {
          this.currentIndex = 0;
        } else {
          this.currentIndex = this.frames.length - 1;
          this.sprite.texture = this.frames[this.currentIndex];
          this.applyFadeOpacity(this.currentIndex);
          this.isPlaying = false;
          const cb = this.completionCallback;
          this.completionCallback = undefined;
          cb?.();
          return;
        }
      }

      this.sprite.texture = this.frames[this.currentIndex];
      this.applyFadeOpacity(this.currentIndex);
    }
  }

  private applyFadeOpacity(index: number): void {
    if (this.fadeInFrames <= 0 && this.fadeOutFrames <= 0) return;
    if (!this.sprite) return;

    const total = this.frames.length;
    let alpha = 1;

    if (this.fadeInFrames > 0 && index < this.fadeInFrames) {
      alpha = (index + 1) / this.fadeInFrames;
    }

    if (this.fadeOutFrames > 0 && index >= total - this.fadeOutFrames) {
      const fadeOutAlpha = (total - index) / this.fadeOutFrames;
      alpha = Math.min(alpha, fadeOutAlpha);
    }

    const clamped = Math.max(0, Math.min(1, alpha));
    if (this.sprite.alpha !== clamped) {
      this.sprite.alpha = clamped;
    }
  }
}
